import { apiRequest } from "../utils/useAPIRequest";
import constant from "../utils/constant";
import { walletStore, transactionStore } from "../stores";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { queryClient } from "../App";

const useGetAllWallets = (walletTableQuery, filterQuery, searchQuery) => {
  const query = useMemo(() => {
    if (searchQuery) {
      return `${walletTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${walletTableQuery}&${filterQuery}`;
    } else {
      return walletTableQuery;
    }
  }, [filterQuery, walletTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getAllWallet", query],
    queryFn: () => {
      return apiRequest({
        url: `/wallets?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const getAllTransactions = async (query) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/transactions/transactions/business?${query}`,
    method: "get",
  });
  if (status === constant.Success) {
    const { transactions, totalCounts } = data || {};

    transactionStore.setTransactions(transactions);
    transactionStore.setTransactionTotalCount(totalCounts);
    return { status };
  }
  return { status, errorMessage };
};

const getBusinessWallet = async (query) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/wallets/admin/business?${query}`,
    method: "get",
  });
  if (status === constant.Success) {
    walletStore.setSelectedBusinessWallet(data);
    return { status };
  }
  return { status, errorMessage };
};

const getWalletBalance = async () => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/wallets/total-wallets",
    method: "get",
  });
  if (status === constant.Success) {
    walletStore.setWalletBalance(data);
    return { status };
  }
  return { status, errorMessage };
};

const getFxInflow = async () => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/transactions/get-admin-inflows",
    method: "get",
  });
  if (status === constant.Success) {
    walletStore.setFxInflow(data);
    return { status };
  }
  return { status, errorMessage };
};

const reviewInflow = async (payload) => {
  const query = `Id=${payload.Id}&action=${payload.action}`;
  const { status, errorMessage } = await apiRequest({
    url: `/transactions/action-admin-inflow?${query}`,
    method: "put",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({ queryKey: ["getOutflow"] });
    return { status };
  }
  return { status, errorMessage };
};

const createInflow = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: `/transactions/create-admin-inflow`,
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({ queryKey: ["getOutflow"] });
    return { status };
  }
  return { status, errorMessage };
};

const getAccountCreateRequests = async () => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/businesses/get-all-bank-requests",
    method: "get",
  });

  if (status === constant.Success) {
    walletStore.setAccCreateRequest(data.businesses);
    return { status };
  }
  return { status, errorMessage };
};

const approveAccountCreateRequest = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: `/businesses/update-bank-account-request`,
    method: "patch",
    payload,
  });
  if (status === constant.Success) {
    await getAccountCreateRequests();
    return { status };
  }
  return { status, errorMessage };
};

const downloadAccountStatement = async (payload) => {
  const query = `startDate=${payload.startDate}&endDate=${payload.endDate}&currencyCode=${payload.currencyCode}&business=${walletStore.businessId}`;

  const { status, errorMessage } = await apiRequest({
    url: `/transactions/admin/accountStatement?${query}`,
    method: "get",
  });

  if (status === constant.Success) {
    return { status: constant.Success };
  }
  return { errorMessage, status };
};

const useGetAllBusinessBankAccounts = (
  BankAccountListTableQuery,
  filterQuery,
  searchQuery
) => {
  const query = useMemo(() => {
    if (searchQuery) {
      return `${BankAccountListTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${BankAccountListTableQuery}&${filterQuery}`;
    } else {
      return BankAccountListTableQuery;
    }
  }, [filterQuery, BankAccountListTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getAllBusinessBankAccounts", query],
    queryFn: () => {
      return apiRequest({
        url: `/bank-accounts?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const useGetOutflow = (outflowTableQuery, filterQuery, searchQuery) => {
  const query = useMemo(() => {
    if (searchQuery) {
      return `${outflowTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${outflowTableQuery}&${filterQuery}`;
    } else {
      return outflowTableQuery;
    }
  }, [filterQuery, outflowTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getOutflow", query],
    queryFn: () => {
      return apiRequest({
        url: `/transactions/get-admin-inflows?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};
const useGetForeignBankAccounts = (
  foreignBankAccountsTableQuery,
  filterQuery,
  searchQuery
) => {
  const query = useMemo(() => {
    if (searchQuery) {
      return `${foreignBankAccountsTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${foreignBankAccountsTableQuery}&${filterQuery}`;
    } else {
      return foreignBankAccountsTableQuery;
    }
  }, [filterQuery, foreignBankAccountsTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getForeignBankAccounts", query],
    queryFn: () => {
      return apiRequest({
        url: `/bank-accounts/foreign-accounts?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const createOutflow = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: `/transactions/create-admin-outflow`,
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({ queryKey: ["getOutflow"] });
    return { status };
  }
  return { status, errorMessage };
};

const reviewOutflow = async (payload) => {
  const query = `Id=${payload.Id}&action=${payload.action}`;
  const { status, errorMessage } = await apiRequest({
    url: `/transactions/action-admin-outflow?${query}`,
    method: "put",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({ queryKey: ["getOutflow"] });
    return { status };
  }
  return { status, errorMessage };
};

const getForeignBankAccounts = async () => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/bank-accounts/foreign-accounts",
    method: "get",
  });

  if (status === constant.Success) {
    walletStore.setForeignBankAccounts(data);
    return { status };
  }
  return { status, errorMessage };
};
const setNGNProviders = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: "/banking/bank/transfer-logic",
    method: "patch",
    payload,
  });

  if (status === constant.Success) {
    queryClient.invalidateQueries({ queryKey: ["getNGNProviders"] });
    return { status };
  }
  return { status, errorMessage };
};
const useGetNGNProviders = () => {
  return useQuery({
    queryKey: ["getNGNProviders"],
    queryFn: () => {
      return apiRequest({
        url: "/banking/bank/get-transfer-logic",
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const useGetBanks = () => {
  return useQuery({
    queryKey: ["getBanks"],
    queryFn: () => {
      return apiRequest({
        url: "/banking/banks",
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const blockFunds = async (payload) => {
  const { status, errorMessage } = await apiRequest({
    url: `/wallets/wallet/lock`,
    method: "patch",
    payload,
  });

  if (status === constant.Success) {
    return { status: constant.Success };
  }
  return { errorMessage, status };
};

const WalletController = {
  getBusinessWallet,
  getWalletBalance,
  getAllTransactions,
  getFxInflow,
  reviewInflow,
  createInflow,
  getAccountCreateRequests,
  approveAccountCreateRequest,
  downloadAccountStatement,
  useGetAllWallets,
  useGetAllBusinessBankAccounts,
  useGetOutflow,
  createOutflow,
  reviewOutflow,
  useGetForeignBankAccounts,
  getForeignBankAccounts,
  setNGNProviders,
  useGetNGNProviders,
  useGetBanks,
  blockFunds,
};

export default WalletController;
